/**
 * AttendanceStatusesPanel — CRUD for per-org attendance status configs.
 *
 * Phase 3 (June 28, 2026): drops directly under the Field Settings tab.
 * org_admin+ can add/edit/delete (with safety: delete is blocked by the
 * backend when the code is in use on any AttendanceMark — surfaced here as
 * a "Deactivate instead" toast).
 *
 * Color picker: keeps things simple by exposing a curated palette of
 * Tailwind class pairs (bg-* + text-*) so the attendance grid renders
 * without extra runtime CSS. A custom-color escape hatch isn't worth the
 * UX cost — admins overwhelmingly pick from a fixed palette.
 */
import { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, Pencil, Plus, Power, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const COLOR_PALETTE = [
  { name: 'Green',   value: 'bg-emerald-500 text-white' },
  { name: 'Red',     value: 'bg-rose-500 text-white' },
  { name: 'Amber',   value: 'bg-amber-500 text-white' },
  { name: 'Orange',  value: 'bg-orange-500 text-white' },
  { name: 'Violet',  value: 'bg-violet-500 text-white' },
  { name: 'Cyan',    value: 'bg-cyan-500 text-white' },
  { name: 'Pink',    value: 'bg-pink-500 text-white' },
  { name: 'Blue',    value: 'bg-warm-2000 text-white' },
  { name: 'Slate',   value: 'bg-slate-400 text-white' },
  { name: 'Muted',   value: 'bg-slate-200 text-slate-600' },
];

const EMPTY = {
  name: '', short_code: '', color: COLOR_PALETTE[0].value,
  is_paid_leave: false,
  counts_as_working_day: true,
  counts_as_present: true,
  attendance_weight: 1.0,
};

export default function AttendanceStatusesPanel() {
  const { get, post, put, del } = useApi();
  const { hasRole } = useAuth();
  const canEdit = hasRole('org_admin');

  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [reorderBusy, setReorderBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await get('/trainees/attendance-statuses?include_inactive=true');
      setStatuses(res.statuses || []);
    } catch {
      toast.error('Failed to load attendance statuses');
    } finally {
      setLoading(false);
    }
  }, [get]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpenDialog(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name, short_code: s.short_code, color: s.color,
      is_paid_leave: s.is_paid_leave,
      counts_as_working_day: s.counts_as_working_day,
      counts_as_present: s.counts_as_present,
      attendance_weight: s.attendance_weight,
    });
    setOpenDialog(true);
  };

  const onSave = async () => {
    if (!form.name.trim() || !form.short_code.trim()) {
      toast.error('Name and short code are required');
      return;
    }
    try {
      if (editing) {
        await put(`/trainees/attendance-statuses/${editing.status_id}`, form);
        toast.success('Status updated');
      } else {
        await post('/trainees/attendance-statuses', form);
        toast.success('Status added');
      }
      setOpenDialog(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const onDelete = async () => {
    if (!confirmDelete) return;
    try {
      await del(`/trainees/attendance-statuses/${confirmDelete.status_id}`);
      toast.success('Status deleted');
      setConfirmDelete(null);
      await load();
    } catch (e) {
      // Backend returns 409 Conflict with a helpful message when the code is
      // in use — surface verbatim so admins know to deactivate instead.
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const toggleActive = async (s) => {
    try {
      await put(`/trainees/attendance-statuses/${s.status_id}`, { is_active: !s.is_active });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const moveRow = async (index, dir) => {
    if (reorderBusy) return;
    const target = index + dir;
    if (target < 0 || target >= statuses.length) return;
    const next = [...statuses];
    [next[index], next[target]] = [next[target], next[index]];
    setStatuses(next);
    setReorderBusy(true);
    try {
      await post('/trainees/attendance-statuses/reorder', {
        status_ids: next.map(s => s.status_id),
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reorder failed');
      await load();
    } finally {
      setReorderBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="attendance-statuses-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-display text-base font-semibold">Attendance Statuses</h3>
          <p className="text-xs text-muted-foreground">
            Configure how each status counts towards attendance % and add custom codes (WFH, Training Leave, etc.).
          </p>
        </div>
        {canEdit && (
          <Button onClick={openCreate} size="sm" data-testid="create-attendance-status-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Status
          </Button>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : statuses.length === 0 ? (
        <p className="text-sm text-muted-foreground">No statuses configured yet.</p>
      ) : (
        <Card className="divide-y">
          {statuses.map((s, idx) => (
            <div key={s.status_id} className="flex items-center gap-3 p-3 hover:bg-muted/40"
                 data-testid={`attendance-status-row-${s.status_id}`}>
              <span className={`inline-flex w-7 h-7 rounded text-[10px] font-bold items-center justify-center ${s.color}`}>
                {s.short_code}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">{s.name}</span>
                  {s.is_system && <Badge variant="outline" className="text-[10px]">System</Badge>}
                  {!s.is_active && <Badge variant="secondary" className="text-[10px]">Inactive</Badge>}
                  {s.is_paid_leave && <Badge variant="outline" className="text-[10px] bg-cyan-50 text-cyan-700 border-cyan-200">Paid leave</Badge>}
                </div>
                <p className="text-xs text-muted-foreground">
                  {s.counts_as_working_day ? 'Counts as working day' : 'Excluded from working days'}
                  {s.counts_as_working_day && (
                    s.counts_as_present
                      ? ` · ${Math.round((s.attendance_weight || 0) * 100)}% present`
                      : ' · 0% present (absent equivalent)'
                  )}
                </p>
              </div>
              {canEdit && (
                <div className="flex items-center gap-1">
                  <Button size="icon" variant="ghost"
                          onClick={() => moveRow(idx, -1)}
                          disabled={idx === 0 || reorderBusy}
                          aria-label="Move up"
                          data-testid={`attendance-status-up-${s.status_id}`}>
                    <ChevronUp className="w-4 h-4" />
                  </Button>
                  <Button size="icon" variant="ghost"
                          onClick={() => moveRow(idx, 1)}
                          disabled={idx === statuses.length - 1 || reorderBusy}
                          aria-label="Move down"
                          data-testid={`attendance-status-down-${s.status_id}`}>
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => toggleActive(s)}
                          title={s.is_active ? 'Deactivate' : 'Activate'}
                          data-testid={`attendance-status-toggle-${s.status_id}`}>
                    <Power className={`w-4 h-4 ${s.is_active ? 'text-emerald-600' : 'text-muted-foreground'}`} />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => openEdit(s)}
                          data-testid={`attendance-status-edit-${s.status_id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  {!s.is_system && (
                    <Button size="icon" variant="ghost" onClick={() => setConfirmDelete(s)}
                            data-testid={`attendance-status-delete-${s.status_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              )}
            </div>
          ))}
        </Card>
      )}

      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent data-testid="attendance-status-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.name}` : 'New attendance status'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label>Name *</Label>
                <Input value={form.name}
                       onChange={e => setForm({ ...form, name: e.target.value })}
                       placeholder="e.g. Work From Home"
                       data-testid="attendance-status-name-input" />
              </div>
              <div>
                <Label>Short code *</Label>
                <Input value={form.short_code}
                       onChange={e => setForm({ ...form, short_code: e.target.value.toUpperCase() })}
                       placeholder="WFH" maxLength={4}
                       data-testid="attendance-status-short-input" />
              </div>
            </div>
            <div>
              <Label>Color</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {COLOR_PALETTE.map(c => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setForm({ ...form, color: c.value })}
                    className={`inline-flex items-center justify-center w-9 h-9 rounded text-[10px] font-bold ${c.value} ${
                      form.color === c.value ? 'ring-2 ring-offset-2 ring-foreground' : ''
                    }`}
                    title={c.name}
                    aria-label={c.name}
                    data-testid={`attendance-status-color-${c.name.toLowerCase()}`}
                  >
                    {form.short_code?.slice(0, 2) || c.name[0]}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2 pt-2 border-t">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Counts as working day</Label>
                  <p className="text-xs text-muted-foreground">If off, the day is excluded from attendance % entirely (Holiday / Week Off).</p>
                </div>
                <Switch checked={form.counts_as_working_day}
                        onCheckedChange={v => setForm({ ...form, counts_as_working_day: v })}
                        data-testid="attendance-status-working-toggle" />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Counts as present</Label>
                  <p className="text-xs text-muted-foreground">If on, the day contributes to the attendance numerator.</p>
                </div>
                <Switch checked={form.counts_as_present}
                        onCheckedChange={v => setForm({ ...form, counts_as_present: v })}
                        disabled={!form.counts_as_working_day}
                        data-testid="attendance-status-present-toggle" />
              </div>
              {form.counts_as_working_day && form.counts_as_present && (
                <div>
                  <Label>Presence weight (0–1)</Label>
                  <Input type="number" step="0.1" min="0" max="1"
                         value={form.attendance_weight}
                         onChange={e => setForm({ ...form, attendance_weight: parseFloat(e.target.value) || 0 })}
                         data-testid="attendance-status-weight-input" />
                  <p className="text-xs text-muted-foreground mt-1">
                    1.0 = full present · 0.5 = half day · 0.0 = absent equivalent.
                  </p>
                </div>
              )}
              <div className="flex items-center justify-between pt-2">
                <div>
                  <Label>Mark as paid leave</Label>
                  <p className="text-xs text-muted-foreground">Informational tag for HR reports — does not change the % math.</p>
                </div>
                <Switch checked={form.is_paid_leave}
                        onCheckedChange={v => setForm({ ...form, is_paid_leave: v })}
                        data-testid="attendance-status-paid-toggle" />
              </div>
            </div>
            {editing?.is_system && (
              <p className="text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 rounded p-2">
                This is a default system status — only display fields (name, short code, color) are editable. Calculation rules are locked to preserve historical attendance %.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button onClick={onSave} data-testid="attendance-status-save-btn">
              {editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete attendance status?</AlertDialogTitle>
            <AlertDialogDescription>
              Removes <strong>{confirmDelete?.name}</strong>. If any historical mark uses this status,
              the delete will be blocked — deactivate it instead to hide it from the picker without losing history.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete} data-testid="confirm-delete-attendance-status-btn">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
